// Business logic services for Assessment

import { CourseProgress, Course } from '../types';

export interface ProgressCalculationResult {
  courseProgressPercentage: number;
  quizProgressPercentage: number;
}

/** Calculates course progress percentage */
export function calculateCourseProgress(
  course: Course | undefined,
  progressCourses: CourseProgress[]
): number {
  if (!course) return 0;

  const courseProgress = progressCourses.find(
    (c) => c.entryUid === course.uid
  );

  return courseProgress?.progress?.percentage || 0;
}

/** Calculates quiz progress percentage */
export function calculateQuizProgress(
  answeredCount: number,
  totalQuestions: number
): number {
  if (totalQuestions === 0) return 0;
  return Math.round((answeredCount / totalQuestions) * 100);
}

/** Checks if any course is incomplete; treats percentage >= 100 as complete when API omits isCompleted. */
export function hasIncompleteCourses(
  progressCourses: CourseProgress[]
): boolean {
  return progressCourses.some((course) => {
    const p = course.progress;
    if (p?.isCompleted === true) return false;
    if ((p?.percentage ?? 0) >= 100) return false;
    return true;
  });
}

/** Finds the index of the first unanswered question */
export function findUnansweredQuestionIndex(
  questions: any[],
  quiz: any[]
): number {
  return questions.findIndex(
    (item) => !quiz?.some((q: any) => q?.question_uid === item.metadata_uid)
  );
}

/**
 * Returns true if every question has an answer in quiz (non-empty selected_options).
 * Used to block opening the submit popup when any question is unanswered.
 */
export function allQuestionsAnswered(
  questions: Array<{ metadata_uid?: string }>,
  quiz: Array<{ question_uid?: string; selected_options?: string | string[] | boolean }>
): boolean {
  if (!questions?.length) return true;
  const quizArray = Array.isArray(quiz) ? quiz : [];
  return questions.every((q) => {
    const item = quizArray.find((x) => x?.question_uid === q?.metadata_uid);
    if (!item) return false;
    const opts = item.selected_options;
    if (opts === undefined || opts === null) return false;
    if (typeof opts === 'boolean') return true;
    if (typeof opts === 'string') return opts.length > 0;
    return Array.isArray(opts) && opts.length > 0;
  });
}

/** Formats quiz answers into the assessment submission shape */
export function formatSubmissionData(quiz: any[]): Array<{
  question_uid: string;
  selected_options: string | string[] | boolean;
}> {
  const quizArray = Array.isArray(quiz) ? quiz : [];
  return quizArray.map((item) => ({
    question_uid: item?.question_uid,
    selected_options: item?.selected_options,
  }));
}

